import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmDialog } from '@/client/components/ConfirmDialog'
import { getTaskStatusColor, getTaskStatusLabel, getOverdueDateColor, getDueTodayColor, getSourceColor } from '@/client/lib/badge-styles'
import { Trash2, Calendar, AlertCircle, Repeat } from 'lucide-react'
import type { Task } from '@/server/modules/tasks/db/schema'
import { useToggleTask, useDeleteTask } from '../hooks/useTasks'
import { cn } from '@/lib/utils'
import {
  formatTaskDate,
  isTaskOverdue,
  isTaskDueToday,
  isTaskCompleted,
  getPriorityColor,
} from '@/client/lib/task-utils'

/**
 * TaskItem component
 * Individual task card with actions
 */
interface TaskItemProps {
  task: Task
  compact?: boolean // For dashboard widget
}

export function TaskItem({ task, compact = false }: TaskItemProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const navigate = useNavigate()
  const toggleTask = useToggleTask()
  const deleteTask = useDeleteTask()

  const handleClick = () => {
    navigate(`/dashboard/tasks/${task.id}`)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent navigation when clicking delete
    setConfirmDelete(true)
  }

  const handleConfirmDelete = () => {
    deleteTask.mutate(task.id)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'open') return null // No badge for open
    const colorClass = getTaskStatusColor(status)
    if (!colorClass) return null
    return (
      <Badge variant="outline" className={colorClass}>
        {getTaskStatusLabel(status)}
      </Badge>
    )
  }

  const isCompleted = isTaskCompleted(task)
  const isOverdue = isTaskOverdue(task)
  const isDueToday = isTaskDueToday(task)

  // Compact mode for dashboard widget
  if (compact) {
    return (
      <div
        onClick={handleClick}
        className={cn(
          'flex items-center gap-3 py-2 px-1 rounded-md hover:bg-accent/50 transition-colors cursor-pointer',
          isCompleted && 'opacity-60'
        )}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isCompleted}
            onCheckedChange={() => toggleTask.mutate(task)}
            disabled={toggleTask.isPending}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm font-medium truncate',
              isCompleted && 'line-through text-muted-foreground'
            )}
          >
            {task.title}
          </p>
        </div>
        {task.recurringParentId && (
          <Repeat className="h-3 w-3 text-blue-500 flex-shrink-0" />
        )}
        {isOverdue && (
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
        )}
        {task.dueAt && !isOverdue && (
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {formatTaskDate(task.dueAt)}
          </span>
        )}
      </div>
    )
  }

  return (
    <Card
      onClick={handleClick}
      className={cn(
        'cursor-pointer hover:bg-accent/30 transition-colors',
        isCompleted && 'opacity-60'
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isCompleted}
                onCheckedChange={() => toggleTask.mutate(task)}
                disabled={toggleTask.isPending}
                className="mt-1"
              />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle
                className={cn(
                  'text-base font-medium',
                  isCompleted && 'line-through text-muted-foreground'
                )}
              >
                {task.title}
              </CardTitle>
              {task.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {task.description}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={getPriorityColor(task.priority)}>
                  {task.priority}
                </Badge>
                {getStatusBadge(task.status)}
                {task.dueAt && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs',
                      isOverdue && getOverdueDateColor(),
                      isDueToday && !isOverdue && getDueTodayColor()
                    )}
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    {isOverdue ? 'Overdue: ' : isDueToday ? 'Today: ' : 'Due: '}
                    {formatTaskDate(task.dueAt)}
                  </Badge>
                )}
                {task.source !== 'manual' && (
                  <Badge variant="outline" className={cn('text-xs', getSourceColor(task.source))}>
                    {task.source === 'ai' ? 'AI' : 'Auto'}
                  </Badge>
                )}
                {task.recurringParentId && (
                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 dark:text-blue-400 dark:border-blue-800">
                    <Repeat className="h-3 w-3 mr-1" />
                    Recurring
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            disabled={deleteTask.isPending}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
      />
    </Card>
  )
}
